import { useCallback, useRef, type KeyboardEvent } from 'react';
import type { ExtendedTimeRange } from '@/types/performance';

interface TimeFrameSelectorProps {
  value: ExtendedTimeRange;
  onChange: (value: ExtendedTimeRange) => void;
  disabled?: boolean;
}

const TIME_FRAME_OPTIONS: Array<{ value: ExtendedTimeRange; label: string }> = [
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '3m', label: '3M' },
  { value: '6m', label: '6M' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1Y' },
  { value: 'all', label: 'All' },
];

export function TimeFrameSelector({ value, onChange, disabled }: TimeFrameSelectorProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      const currentIndex = TIME_FRAME_OPTIONS.findIndex((opt) => opt.value === value);
      let nextIndex = currentIndex;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (currentIndex + 1) % TIME_FRAME_OPTIONS.length;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (currentIndex - 1 + TIME_FRAME_OPTIONS.length) % TIME_FRAME_OPTIONS.length;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = TIME_FRAME_OPTIONS.length - 1;
      } else {
        return;
      }

      const nextOption = TIME_FRAME_OPTIONS[nextIndex];
      if (nextOption) {
        onChange(nextOption.value);
      }

      // Move focus to the newly selected button
      const buttons = groupRef.current?.querySelectorAll('button');
      if (buttons) {
        const nextButton = buttons[nextIndex];
        if (nextButton) {
          nextButton.focus();
        }
      }
    },
    [value, onChange, disabled]
  );

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label="Time frame selection"
      className="flex gap-1 rounded-lg bg-dark-800 p-1"
    >
      {TIME_FRAME_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => !disabled && onChange(option.value)}
          onKeyDown={handleKeyDown}
          aria-pressed={value === option.value}
          disabled={disabled}
          tabIndex={value === option.value ? 0 : -1}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
            value === option.value
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-dark-400 hover:text-dark-200 hover:bg-dark-700'
          } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
